import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  CalendarClock,
  Hourglass,
  ArrowUpCircle,
  Repeat,
  Compass,
  ShieldCheck,
  Map,
  Route as RouteIcon,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import WebCenterStackDiagram from '@/components/diagrams/WebCenterStackDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const PAGE_TITLE =
  'Oracle Fusion Middleware 12c End of Support — The Complete Guide | EZ Cloud';
const PAGE_DESCRIPTION =
  'Oracle Fusion Middleware 12c Premier Support ends December 2026, Extended Support December 2027. A complete, component-by-component guide to the cliff — WebCenter, SOA Suite, WebLogic, Forms, Identity Management, Data Integrator, BI — which have a 14c upgrade path, which must be replaced, and how to plan each.';
const CANONICAL = 'https://ezcloud.ai/solutions/oracle-fusion-middleware-12c-end-of-support';

const KEY_DATES = [
  {
    date: 'December 2026',
    label: 'Fusion Middleware 12c Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c (12.2.1.4) reaches the end of Premier Support on the normal Lifetime Support schedule. After this date there are no new features, no new operating-system or database certifications, and no non-critical fixes across the 12c stack — WebCenter, SOA Suite, WebLogic, Forms, Identity Management, Data Integrator, and BI included.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'Fusion Middleware 12c Extended Support ends',
    detail:
      "Per Oracle's Lifetime Support Policy, Extended Support for Fusion Middleware 12c ends December 2027. Extended Support provides security updates and Severity 1 fixes; after it ends, Sustaining Support continues but without new patches or certifications. This defines the practical decision window: plan through 2026, execute before the 2027 boundary.",
    severity: 'red',
  },
  {
    date: '~8 years',
    label: 'Fusion Middleware 14c support runway',
    detail:
      'Oracle Fusion Middleware 14c is release 14.1.2. For the components with a 14c path, upgrading resets the clock to a fresh Premier-plus-Extended window — roughly eight years of supported runway from the 14c release. Upgrades to 14.1.2 are supported only from 12.2.1.4 (or 12.2.1.19); older 12c releases must first move to 12.2.1.4.',
    severity: 'green',
  },
];

const COMPONENTS = [
  {
    name: 'WebCenter',
    scope: 'Content (UCM), Imaging, Enterprise Capture, Forms Recognition, Portal',
    verdict: 'upgrade',
    path: '14c (14.1.2) upgrade path. Cloud-native on OCI, REST APIs across the stack, AI integration points, OCI Object Storage as a content tier.',
    links: [
      { label: 'WebCenter 14c upgrade', to: '/solutions/webcenter-14c-upgrade' },
      { label: 'Modernize Oracle WebCenter', to: '/solutions/modernize-oracle-webcenter' },
    ],
  },
  {
    name: 'SOA Suite',
    scope: 'BPEL, Mediator, Business Rules, composites',
    verdict: 'upgrade',
    path: '14c (14.1.2) upgrade path. Composites migrate forward via the Upgrade Assistant, with validation for 11g-era patterns.',
    links: [{ label: 'SOA Suite 14c migration', to: '/solutions/oracle-soa-suite-migration' }],
  },
  {
    name: 'WebLogic Server',
    scope: 'The application-server tier under the whole stack',
    verdict: 'upgrade',
    path: '14c (14.1.2) upgrade path. The 14c application server is the foundation the other 14c components run on.',
    links: [{ label: 'WebLogic 14c upgrade', to: '/solutions/oracle-weblogic-14c-upgrade' }],
  },
  {
    name: 'BPM Suite',
    scope: 'Business process management, human workflow',
    verdict: 'upgrade',
    path: '14c (14.1.2) upgrade path, alongside SOA Suite.',
    links: [{ label: 'BPM Suite migration', to: '/solutions/oracle-bpm-suite-migration' }],
  },
  {
    name: 'ADF',
    scope: 'Application Development Framework — including EBS non-PO coding forms',
    verdict: 'upgrade',
    path: '14c (14.1.2) upgrade path. Customizations need an ADF 14c compatibility review.',
    links: [
      {
        label: 'ADF non-PO coding form modernization',
        to: '/solutions/oracle-adf-non-po-coding-form-modernization',
      },
    ],
  },
  {
    name: 'Oracle Forms & Reports',
    scope: 'Forms and Reports developer platform',
    verdict: 'upgrade',
    path: 'Oracle Forms & Reports 14c (14.1.2) was released February 2025. Adds HTTP/2, TLS 1.3, Java Virtual Threads, and no database credentials required at deployment. Upgrade in-place (same Oracle Home, faster, irreversible without a backup) or side-by-side (new Oracle Home, lower risk, 12c keeps running). It is a full upgrade, not a patch set.',
    links: [
      { label: 'Forms 12c to 14c upgrade', to: '/solutions/oracle-forms-12c-to-14c-upgrade' },
    ],
  },
  {
    name: 'Identity & Access Management',
    scope: 'OAM, OIG, OUD, OID',
    verdict: 'upgrade',
    path: 'Oracle IAM 14c (14.1.2.1) was released March 2025 — Access Management (OAM), Identity Governance (OIG), Unified Directory (OUD), and Internet Directory (OID). Runs on WebLogic Server 14c; certified with Oracle DB 19c/23c/23ai and JDK 17 (certified with JDK 21). The 12c PS4 → 14c upgrade is supported both in-place and out-of-place, and carries roughly eight years of Premier-plus-Extended support from release.',
    links: [
      {
        label: 'Identity Management 12c to 14c upgrade',
        to: '/solutions/oracle-identity-management-12c-to-14c-upgrade',
      },
    ],
  },
  {
    name: 'Data Integrator (ODI)',
    scope: 'ELT / data integration',
    verdict: 'upgrade',
    path: 'Oracle Data Integrator 14c (14.1.2) supports upgrade from 12.2.1.4. Repositories are upgraded with the Upgrade Assistant; the repository upgrade is irreversible, so a verified backup is critical before you run it.',
    links: [
      { label: 'Data Integrator 12c to 14c upgrade', to: '/solutions/oracle-data-integrator-12c-to-14c-upgrade' },
    ],
  },
  {
    name: 'OBIEE / Oracle BI',
    scope: 'Business intelligence, analytics',
    verdict: 'replace',
    path: 'There is no 14c successor for OBIEE / Oracle BI 12c. The forward path is Oracle Analytics Server (OAS, on-premises) or Oracle Analytics Cloud (OAC). Note that OBIEE 12c Extended Support already ended August 2025, so this is the most time-sensitive component in the stack.',
    links: [],
  },
  {
    name: 'Oracle Endeca',
    scope: 'Guided search and discovery',
    verdict: 'replace',
    path: 'Oracle Endeca has reached end of life with no direct successor product. Forward planning means selecting a replacement search or discovery platform for the capability, rather than upgrading in place.',
    links: [],
  },
];

const DECISION_KINDS = [
  {
    icon: ArrowUpCircle,
    title: 'Upgrade — most of the stack',
    body: 'WebCenter, SOA Suite, WebLogic, BPM, ADF, Oracle Forms & Reports, Identity & Access Management, and Data Integrator all have a supported 12c → 14c (14.1.2) upgrade path. The work is real — inventory, out-of-place or side-by-side upgrade, customization migration, and validation — but the architecture and your investment carry forward, and you reset to a fresh support window.',
  },
  {
    icon: Repeat,
    title: 'Replace — BI and Endeca',
    body: 'Two components have no 14c path. OBIEE / Oracle BI 12c moves to Oracle Analytics Server or Oracle Analytics Cloud, and Oracle Endeca (end of life) needs a replacement for the search/discovery capability. These are re-platforming decisions, not upgrades, and OBIEE 12c Extended Support already ended in August 2025 — so BI usually leads the sequencing.',
  },
];

const HELP = [
  {
    icon: ShieldCheck,
    title: 'Components EZ Cloud delivers directly',
    body: 'Founder-led modernization for WebCenter, SOA Suite, WebLogic, BPM, ADF, Identity & Access Management, and the Oracle Forms platform upgrade. Andrew reads the running 12c implementation directly — Forms Recognition rules, SOA composites, WebLogic configuration, ADF customizations, EBS / Fusion integration — and plans the 14c path against your specific stack.',
  },
  {
    icon: Compass,
    title: 'The components without a 14c path',
    body: 'Two parts of the 12c stack do not have a 14c upgrade and are replaced instead: Oracle BI (OBIEE) moves to Oracle Analytics Server or Cloud, and Oracle Endeca has reached end of life. This guide maps those paths honestly so you have one clear forward plan for the whole estate — not just the pieces with a straight upgrade.',
  },
  {
    icon: Map,
    title: 'One plan across every component',
    body: 'The value of starting at the hub is sequencing. A Decision Call produces a component-by-component map — what upgrades, what replaces, what leads (BI, given its August 2025 Extended Support end), and how the pieces fit — so procurement and stakeholders work from one coherent timeline into the December 2026 / 2027 window.',
  },
];

const FAQS = [
  {
    q: 'When does Oracle Fusion Middleware 12c support end?',
    a: 'Oracle Fusion Middleware 12c (12.2.1.4) Premier Support ends December 2026 and Extended Support ends December 2027, per Oracle’s Lifetime Support Policy. This applies across the 12c stack — WebCenter, SOA Suite, WebLogic, Forms, Identity Management, Data Integrator, and BI. The practical decision window is 2026 for planning and before the 2027 boundary for execution.',
  },
  {
    q: 'Is this the same as Oracle Database 12c end of support?',
    a: 'No — they are different products with different timelines. Oracle Database 12c is the database product, and its support windows ended years earlier under its own lifecycle. This page covers Oracle Fusion Middleware 12c (release 12.2.1.4) — the middleware stack that includes WebCenter, SOA Suite, WebLogic Server, Forms, Identity Management, and Data Integrator — whose Premier Support ends December 2026 and Extended Support ends December 2027. If you searched for "Oracle 12c end of support" meaning the database, this is not that page; if you run any Fusion Middleware 12c component, you are in the right place.',
  },
  {
    q: 'Which Fusion Middleware components have a 14c upgrade path?',
    a: 'Most do. WebCenter, SOA Suite, WebLogic Server, BPM Suite, ADF, Oracle Forms & Reports, Identity & Access Management, and Data Integrator all have a supported 12c → 14c (release 14.1.2) upgrade path. Two components do not: OBIEE / Oracle BI and Oracle Endeca. Those two are replace decisions rather than upgrades.',
  },
  {
    q: 'What is the difference between an upgrade and a replace decision here?',
    a: 'An upgrade moves a component from 12c to 14c (14.1.2) using Oracle’s Upgrade Assistant and supported patterns — the architecture and your customizations carry forward, and you reset to a fresh support window. A replace decision applies where there is no 14c successor: OBIEE / Oracle BI moves to Oracle Analytics Server or Oracle Analytics Cloud, and Oracle Endeca (end of life) needs a new search/discovery platform. Replace is a re-platforming project, not a version upgrade.',
  },
  {
    q: 'What is new in Oracle Forms & Reports 14c?',
    a: 'Oracle Forms & Reports 14c (14.1.2) was released February 2025 and adds HTTP/2, TLS 1.3, Java Virtual Threads, and the removal of the requirement for database credentials at deployment. It is a full upgrade, not a patch set, and you can upgrade in-place (same Oracle Home, faster but irreversible without a backup) or side-by-side (new Oracle Home, lower risk, with 12c still running). Our dedicated page covers the Forms 12c to 14c upgrade patterns in detail.',
  },
  {
    q: 'Is there a 14c path for Oracle Identity & Access Management?',
    a: 'Yes. Oracle IAM 14c (14.1.2.1) was released March 2025 and includes Access Management (OAM), Identity Governance (OIG), Unified Directory (OUD), and Internet Directory (OID). It runs on WebLogic Server 14c and is certified with Oracle DB 19c/23c/23ai and JDK 17 (certified with JDK 21), with the 12c PS4 → 14c upgrade supported both in-place and out-of-place. It carries roughly eight years of Premier-plus-Extended support from the March 2025 release.',
  },
  {
    q: 'How does the Data Integrator (ODI) upgrade work?',
    a: 'Oracle Data Integrator 14c (14.1.2) supports an upgrade from 12.2.1.4, with repositories upgraded via the Upgrade Assistant. The single most important point is that the repository upgrade is irreversible — a verified backup before you run the Upgrade Assistant is critical. See the dedicated Data Integrator 12c to 14c upgrade page for the full sequence.',
  },
  {
    q: 'What happens to OBIEE / Oracle BI, since there is no 14c?',
    a: 'OBIEE / Oracle BI 12c has no 14c successor, so the forward path is Oracle Analytics Server (OAS, on-premises) or Oracle Analytics Cloud (OAC). This is the most time-sensitive component in the stack because OBIEE 12c Extended Support already ended in August 2025, ahead of the rest of Fusion Middleware 12c. It usually leads the sequencing of a whole-stack plan.',
  },
  {
    q: 'Where should we start?',
    a: 'Start with an inventory of which Fusion Middleware 12c components you actually run and which of them upgrade to 14c versus need replacing. BI (Extended Support ended August 2025) typically leads, then the 14c-capable components sequence toward the December 2026 / 2027 window. A Decision Call with EZ Cloud produces a component-by-component map across the whole stack; our WebCenter Decision Guide is a good companion resource for the framework.',
  },
];

const OracleFusionMiddleware12cEndOfSupport = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const schemaAbout = [
    'Oracle Fusion Middleware',
    'Oracle Fusion Middleware 12c',
    'Oracle Fusion Middleware 14c',
    'Oracle Fusion Middleware End of Support',
    'Oracle Lifetime Support Policy',
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-07-06',
    dateModified: '2026-07-06',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': CANONICAL,
    },
    about: schemaAbout.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={CANONICAL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Fusion Middleware 12c End of Support',
            url: CANONICAL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-07-06" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <CalendarClock className="w-4 h-4" aria-hidden />
              The complete FMW 12c cliff guide · December 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle Fusion Middleware 12c end of support
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                The complete component-by-component guide to the cliff, and the forward path for each.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Premier Support ends December 2026, Extended December 2027. Most components have a
              supported 12c → 14c (14.1.2) upgrade path; two — BI and Endeca — must be replaced. This
              is the hub that routes you to the right forward path for every part of your stack.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('fmw-12c-hub-hero')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book a Fusion Middleware Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/resources/webcenter-decision-guide">Read the Decision Guide</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — direct answer for AI Overviews */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The short answer
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle Fusion Middleware 12c end of support, in brief
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Fusion Middleware 12c (12.2.1.4) Premier Support ends December 2026 and
                  Extended Support ends December 2027,
                </strong>{' '}
                per Oracle&apos;s Lifetime Support Policy. This is the normal Lifetime Support schedule,
                and it applies across the whole 12c stack — WebCenter, SOA Suite, WebLogic, Forms,
                Identity Management, Data Integrator, and BI.
              </p>
              <p>
                <strong className="text-gray-900">
                  Most components have a supported 12c → 14c (release 14.1.2) upgrade path
                </strong>{' '}
                — WebCenter, SOA Suite, WebLogic Server, BPM Suite, ADF, Oracle Forms &amp; Reports,
                Identity &amp; Access Management, and Data Integrator. Two do not: OBIEE / Oracle BI
                and Oracle Endeca. Those are replace decisions rather than upgrades, and OBIEE 12c
                Extended Support already ended August 2025, so BI is the most time-sensitive piece.
              </p>
              <p>
                Upgrades to 14.1.2 are supported only from 12.2.1.4 (or 12.2.1.19); older 12c
                releases must first move to 12.2.1.4. The right first step is a component-by-component
                inventory — which of your components upgrade, which replace, and in what order — into
                the December 2026 / 2027 window.
              </p>
            </div>
          </div>
        </section>

        {/* Key dates */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Key dates for the Fusion Middleware 12c cliff
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle&apos;s Lifetime Support Policy. The 12c support boundary and the 14c runway
              together frame the decision for every Fusion Middleware customer.
            </p>
            <div className="space-y-5">
              {KEY_DATES.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* THE CORE SECTION — component-by-component */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Your component, your forward path
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Every Fusion Middleware 12c component, and where it goes next
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              Find the component you run, see whether it has a 14c (14.1.2) upgrade path or needs to be
              replaced, and follow the link to the detailed forward plan where one exists.
            </p>
            <div className="max-w-5xl mx-auto mb-12">
              <WebCenterStackDiagram />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {COMPONENTS.map((c) => (
                <Card key={c.name} className="p-6 bg-white border border-gray-200 shadow-sm flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{c.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{c.scope}</p>
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        c.verdict === 'upgrade'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      {c.verdict === 'upgrade' ? (
                        <>
                          <ArrowUpCircle className="w-3.5 h-3.5" aria-hidden />
                          14c upgrade
                        </>
                      ) : (
                        <>
                          <Repeat className="w-3.5 h-3.5" aria-hidden />
                          Replace
                        </>
                      )}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm flex-1">{c.path}</p>
                  {c.links.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-gray-100 pt-4">
                      {c.links.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          className="inline-flex items-center text-sm font-semibold text-primary hover:text-accent transition-colors"
                        >
                          {l.label}
                          <ArrowRight className="w-3.5 h-3.5 ml-1" aria-hidden />
                        </Link>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Upgrade vs replace */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Two kinds of decision
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Upgrade vs replace
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Fusion Middleware 12c stack splits cleanly into two decision types. Naming which one
              each component is is the whole point of the hub.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {DECISION_KINDS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-bold text-lg mb-2">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Version prerequisite note */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-accent/5 border border-accent/30 shadow-sm">
              <div className="flex items-start gap-4">
                <RouteIcon className="w-6 h-6 text-accent flex-shrink-0 mt-1" aria-hidden />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    Before you plan any 14c upgrade
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    14c upgrades start from 12.2.1.4
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Oracle supports upgrades to 14c (14.1.2) only from 12.2.1.4 (or 12.2.1.19). If a
                    component is running an older 12c release, the first move is to 12.2.1.4, and the
                    14c upgrade follows from there. Confirming the current baseline of each component is
                    the first thing an inventory establishes, because it changes the number of steps in
                    the path.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* How EZ Cloud helps */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud helps
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Founder-led Fusion Middleware modernization
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              EZ Cloud delivers the middleware components below founder-led, and maps the full cliff —
              including the parts that replace rather than upgrade — so you get one clear plan across
              the whole stack.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {HELP.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-gray-900 font-bold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured framework behind the forward paths on this page — diagnostic questions,
              stack-specific considerations, and a vendor evaluation checklist for the 2026 / 2027
              window.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the Fusion Middleware 12c cliff
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="flex items-center justify-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Hourglass className="w-4 h-4" aria-hidden />
              Plan through 2026, execute before 2027
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Map your whole Fusion Middleware stack
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              specialist with 20+ years inside Oracle Fusion Middleware — WebCenter, SOA Suite,
              WebLogic, ADF, Forms, and Identity Management. Walk through which of your components
              upgrade to 14c, which replace, and in what order, in the context of your specific stack.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('fmw-12c-hub-cta')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book a Fusion Middleware Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleFusionMiddleware12cEndOfSupport;
